import React, { useState } from 'react';
import { Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from 'chart.js';
import '../sip/Calculators.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const Retirement = () => {
    const [currentAge, setCurrentAge] = useState(30);
    const [retirementAge, setRetirementAge] = useState(60);
    const [lifeExpectancy, setLifeExpectancy] = useState(85);
    const [monthlyExpense, setMonthlyExpense] = useState(50000);
    const [inflationRate, setInflationRate] = useState(6);
    const [preRetirementReturn, setPreRetirementReturn] = useState(12);
    const [postRetirementReturn, setPostRetirementReturn] = useState(8);
    const [sipReturn] = useState(12);
    const [lumpsumReturn] = useState(12);

    const [results, setResults] = useState(null);
    const [pieData, setPieData] = useState(null);

    const calculateRetirement = () => {
        const retirementYears = lifeExpectancy - retirementAge;
        const yearsToRetire = retirementAge - currentAge;

        const adjMonthlyExpense = monthlyExpense * Math.pow(1 + inflationRate / 100, yearsToRetire);
        const yearlyExpense = adjMonthlyExpense * 12;

        const corpusRequired = yearlyExpense * ((1 - Math.pow(1 + postRetirementReturn / 100, -retirementYears)) / (postRetirementReturn / 100));

        const sipMonths = yearsToRetire * 12;
        const monthlyRate = sipReturn / 100 / 12;
        const sipAmount = corpusRequired / (((Math.pow(1 + monthlyRate, sipMonths) - 1) / monthlyRate) * (1 + monthlyRate));

        const lumpsumInvestment = corpusRequired / Math.pow(1 + lumpsumReturn / 100, yearsToRetire);

        setResults({
            monthlyExpensesAtRetirement: Math.round(adjMonthlyExpense),
            requiredCorpus: Math.round(corpusRequired),
            sipAmount: Math.round(sipAmount),
            lumpsumInvestment: Math.round(lumpsumInvestment),
        });

        setPieData({
            labels: ['Required Corpus at Retirement', 'SIP Contribution', 'Lumpsum Investment'],
            datasets: [
                {
                    data: [corpusRequired, sipAmount * sipMonths, lumpsumInvestment],
                    backgroundColor: ['#004aad', '#76d041', '#ffa600'],
                    borderWidth: 1,
                },
            ],
        });
    };

    return (
        <div className="sip-calculator">
            <h2>Retirement Planning Calculator</h2>

            <div className="input-group">
                <label>Current Age</label>
                <input type="range" min="0" max="100" value={currentAge} onChange={(e) => setCurrentAge(Number(e.target.value))} />
                <input
                    type="number"
                    min="0"
                    max="100"
                    value={currentAge}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                            setCurrentAge('');
                        } else {
                            setCurrentAge(Number(val));
                        }
                    }}
                    className="value-box"
                />
            </div>

            <div className="input-group">
                <label>Retirement Age</label>
                <input type="range" min="0" max="100" value={retirementAge} onChange={(e) => setRetirementAge(Number(e.target.value))} />
                <input
                    type="number"
                    min="1"
                    max="100"
                    value={retirementAge}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                            setRetirementAge('');
                        } else {
                            setRetirementAge(Number(val));
                        }
                    }}
                    className="value-box"
                />
            </div>

            <div className="input-group">
                <label>Life Expectancy</label>
                <input type="range" min="0" max="100" value={lifeExpectancy} onChange={(e) => setLifeExpectancy(Number(e.target.value))} />
                <input
                    type="number"
                    min="0"
                    max="100"
                    value={lifeExpectancy}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                            setLifeExpectancy('');
                        } else {
                            setLifeExpectancy(Number(val));
                        }
                    }}
                    className="value-box"
                />
            </div>

            <div className="input-group">
                <label>Monthly Household Expense (₹)</label>
                <input type="range" min="0" max="100000" step="1000" value={monthlyExpense} onChange={(e) => setMonthlyExpense(Number(e.target.value))} />
                <input
                    type="number"
                    min="1"
                    max="100000"
                    value={monthlyExpense}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                            setMonthlyExpense('');
                        } else {
                            setMonthlyExpense(Number(val));
                        }
                    }}
                    className="value-box"
                />
            </div>

            <div className="input-group">
                <label>Inflation Rate (%)</label>
                <input type="range" min="0" max="100" step="0.1" value={inflationRate} onChange={(e) => setInflationRate(Number(e.target.value))} />
                <input
                    type="number"
                    min="0"
                    max="100"
                    value={inflationRate}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                            setInflationRate('');
                        } else {
                            setInflationRate(Number(val));
                        }
                    }}
                    className="value-box"
                />
            </div>

            <div className="input-group">
                <label>Expected Returns (Pre Retirement) (%)</label>
                <input type="range" min="0" max="100" step="1" value={preRetirementReturn} onChange={(e) => setPreRetirementReturn(Number(e.target.value))} />
                <input
                    type="number"
                    min="0"
                    max="100"
                    value={preRetirementReturn}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                            setPreRetirementReturn('');
                        } else {
                            setPostRetirementReturn(Number(val));
                        }
                    }}
                    className="value-box"
                />
            </div>

            <div className="input-group">
                <label>Expected Returns (Post Retirement) (%)</label>
                <input type="range" min="0" max="100" step="1" value={postRetirementReturn} onChange={(e) => setPostRetirementReturn(Number(e.target.value))} />
                <input
                    type="number"
                    min="0"
                    max="100"
                    value={postRetirementReturn}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                            setPostRetirementReturn('');
                        } else {
                            setPostRetirementReturn(Number(val));
                        }
                    }}
                    className="value-box"
                />
            </div>

            <button className="calculate-btn" onClick={calculateRetirement}>Calculate</button>

            {results && pieData && (
                <div className="sip-output-section">
                    <div className="result-summary">
                        <h3>Results</h3>
                        <p>Monthly Expenses at Retirement: <span>₹{results.monthlyExpensesAtRetirement.toLocaleString()}</span></p>
                        <p>Required Lumpsum Corpus: <span>₹{results.requiredCorpus.toLocaleString()}</span></p>
                        <p>Planning Through SIP (Monthly): <span>₹{results.sipAmount.toLocaleString()}</span></p>
                        <p>Planning Through Lumpsum: <span>₹{results.lumpsumInvestment.toLocaleString()}</span></p>
                    </div>

                    <div className="pie-chart-container">
                        <h3>Corpus Distribution</h3>
                        <Pie data={pieData} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Retirement;
